/**
 * Privacy Center Page
 *
 * GDPR compliance hub for user data rights:
 * - Download all personal data (data portability)
 * - Delete account (right to erasure)
 * - Manage cookie preferences
 * - View privacy rights information
 */

import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Link as MuiLink,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Download as DownloadIcon,
  DeleteForever as DeleteIcon,
  Cookie as CookieIcon,
  Security as SecurityIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authService, userService } from '../../services';

const PrivacyCenter: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [downloading, setDownloading] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDownloadData = async () => {
    setDownloading(true);
    setDownloadError(null);
    setDownloadSuccess(false);

    try {
      const token = authService.getAccessToken();
      if (!token) {
        throw new Error('Not authenticated. Please log in to download your data.');
      }

      const response = await fetch('http://localhost:3000/api/v2/users/me/export', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download data');
      }

      // Get the JSON data
      const data = await response.json();

      // Create a downloadable file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `my-data-export-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setDownloadSuccess(true);
      setTimeout(() => setDownloadSuccess(false), 5000);
    } catch (error) {
      console.error('Download error:', error);
      setDownloadError(
        error instanceof Error ? error.message : 'Failed to download your data. Please try again.'
      );
    } finally {
      setDownloading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    setDeleteError(null);

    try {
      await userService.deleteAccount();
      // User is logged out, need to clear auth state and redirect
      await logout();
    } catch (error) {
      console.error('Delete error:', error);
      setDeleteError(
        error instanceof Error
          ? error.message
          : 'Failed to delete your account. Please try again.'
      );
      setDeleting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Privacy Center
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your privacy settings and exercise your data rights
        </Typography>
      </Box>

      {/* Success/Error Messages */}
      {downloadSuccess && (
        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
          Your data has been downloaded successfully!
        </Alert>
      )}

      {downloadError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setDownloadError(null)}>
          {downloadError}
        </Alert>
      )}

      {deleteError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setDeleteError(null)}>
          {deleteError}
        </Alert>
      )}

      {/* Download Your Data */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <DownloadIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Download Your Data</Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Get a copy of all your personal data stored in our system. This includes your profile
            information, preferences, security settings, organizations, teams, and content.
          </Typography>

          <Box
            sx={{
              bgcolor: alpha(theme.palette.info.main, 0.1),
              border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
              borderRadius: 1,
              p: 2,
              mb: 3,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              <strong>What's included:</strong> Profile data, preferences (including cookie
              consent), security settings, organization memberships, team memberships, and
              user-created content metadata
            </Typography>
          </Box>

          <Button
            variant="contained"
            color="primary"
            startIcon={downloading ? <CircularProgress size={20} /> : <DownloadIcon />}
            onClick={handleDownloadData}
            disabled={downloading}
          >
            {downloading ? 'Preparing Download...' : 'Download My Data'}
          </Button>
        </CardContent>
      </Card>

      {/* Cookie Preferences */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CookieIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Cookie Preferences</Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Manage your cookie consent choices. You can change what types of cookies we use and
            withdraw consent at any time.
          </Typography>

          <Button
            variant="outlined"
            color="primary"
            component={Link}
            to="/profile?section=privacy"
            startIcon={<CookieIcon />}
          >
            Manage Cookie Settings
          </Button>
        </CardContent>
      </Card>

      {/* Your Privacy Rights */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SecurityIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Your Privacy Rights</Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Under data protection laws (including GDPR), you have the following rights:
          </Typography>

          <List dense>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Right to Access"
                secondary="Download a copy of your personal data"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Right to Rectification"
                secondary="Update or correct your personal information"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Right to Erasure"
                secondary="Request deletion of your account and personal data"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Right to Data Portability"
                secondary="Receive your data in a machine-readable format"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Right to Object"
                secondary="Object to processing of your personal data"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Right to Withdraw Consent"
                secondary="Withdraw your consent for data processing at any time"
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="caption" color="text.secondary">
            Learn more about our data practices in our{' '}
            <MuiLink component={Link} to="/legal/privacy" underline="hover">
              Privacy Policy
            </MuiLink>{' '}
            and{' '}
            <MuiLink component={Link} to="/legal/cookies" underline="hover">
              Cookie Policy
            </MuiLink>
            .
          </Typography>
        </CardContent>
      </Card>

      {/* Delete Account (Danger Zone) */}
      <Card
        sx={{
          border: `2px solid ${theme.palette.error.main}`,
          bgcolor: alpha(theme.palette.error.main, 0.05),
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <WarningIcon sx={{ mr: 1, color: 'error.main' }} />
            <Typography variant="h6" color="error">
              Danger Zone
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Permanently delete your account and all associated data. This action cannot be undone.
          </Typography>

          <Alert severity="warning" icon={<InfoIcon />} sx={{ mb: 3 }}>
            <strong>Before deleting:</strong> We recommend downloading your data first. Once
            deleted, all your information will be permanently removed and cannot be recovered.
          </Alert>

          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialogOpen(true)}
          >
            Delete My Account
          </Button>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Account?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete your account? This will:
          </DialogContentText>
          <List dense>
            <ListItem>
              <ListItemText primary="• Remove all your personal information" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Delete all your organizations and teams" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Remove all your content and data" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Log you out of all devices" />
            </ListItem>
          </List>
          <DialogContentText sx={{ mt: 2 }}>
            <strong>This action cannot be undone.</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAccount}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete My Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PrivacyCenter;
